import React, { useCallback, useRef, useState } from "react";
import * as Mui from "@mui/material";
import {
  designColors,
  designRadii,
  designType,
} from "services/constants/designTokens";
import { IconTile } from "components/DesignSystem/IconSectionCard";

// Shared building blocks for the Team Workspace > Settings screens (Check-in
// Policy, Geo-Fenced Sites): the side-panel header with its icon tile, the
// icon-led field blocks, the tinted section cards and the teal switches.

// The rows a list shows: what CustomFilter produced (search, filters, sort),
// or the loaded data itself until CustomFilter has caught up with it.
//
// CustomFilter reports its result from an effect, one render after the data
// changes. It also reports an empty result while the list is still loading.
// Using that result as it stands made the table show "No data available"
// for a moment after the rows arrived. So each result is kept with the data
// it was computed from, and ignored once the data has moved on.
//   const { rows, ready, onFilteredDataChange } = useFilteredRows(data);
export const useFilteredRows = (data) => {
  const dataRef = useRef(data);
  dataRef.current = data;
  const [filtered, setFiltered] = useState(null);
  const onFilteredDataChange = useCallback(
    (rows) => setFiltered({ source: dataRef.current, rows }),
    [],
  );
  const ready = filtered !== null && filtered.source === data;
  return { rows: ready ? filtered.rows : data, ready, onFilteredDataChange };
};

export const YES_COLOR = "#0BB783";
export const NO_COLOR = "#F64E60";

// designTheme keeps MUI's default blue primary, so switches are set to the
// app's teal here (same override as Administration > Punch Processing).
export const tealSwitchSx = {
  "& .MuiSwitch-switchBase.Mui-checked": { color: designColors.tealDark },
  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
    backgroundColor: designColors.teal,
  },
};


// GlobalSidePanel `header` slot: icon tile, title and helper line. The tile
// is left out when no icon is passed.
export const PanelIconHeader = ({ icon, title, subtitle, trailing }) => (
  <Mui.Stack
    direction="row"
    spacing={1.5}
    alignItems="center"
    sx={{ minWidth: 0, flex: 1 }}
  >
    {icon ? <IconTile icon={icon} size={44} /> : null}
    <Mui.Box sx={{ minWidth: 0 }}>
      <Mui.Stack direction="row" spacing={1} alignItems="center">
        <Mui.Typography noWrap sx={{ ...designType.panelTitle, lineHeight: 1.3 }}>
          {title}
        </Mui.Typography>
        {trailing}
      </Mui.Stack>
      {subtitle ? (
        <Mui.Typography sx={{ ...designType.helperText, mt: 0.25 }}>
          {subtitle}
        </Mui.Typography>
      ) : null}
    </Mui.Box>
  </Mui.Stack>
);

// Field labels in these panels use the same type as GlobalFieldLabel (the
// label every other form uses): 14px, medium weight.
const fieldLabelSx = {
  fontSize: 14,
  fontWeight: 500,
  color: designColors.slate900,
  lineHeight: 1.35,
};

// A block with no field under it ("Tracking Rules") is a section heading and
// keeps the heavier heading type.
const blockHeadingSx = {
  fontSize: 15,
  fontWeight: 600,
  color: designColors.slate900,
};

// A titled block that starts with a small icon tile ("Scope", "Tracking
// Rules"); its children sit under the title, aligned with the text. Without
// an icon the title and children start at the left edge.
export const IconFieldBlock = ({ icon, title, helper, children }) => (
  <Mui.Stack direction="row" spacing={1.5} alignItems="flex-start">
    {icon ? <IconTile icon={icon} size={38} /> : null}
    <Mui.Box sx={{ flex: 1, minWidth: 0 }}>
      <Mui.Typography sx={children ? fieldLabelSx : blockHeadingSx}>
        {title}
      </Mui.Typography>
      {helper ? (
        <Mui.Typography sx={{ ...designType.helperText, mt: 0.2 }}>
          {helper}
        </Mui.Typography>
      ) : null}
      {children ? <Mui.Box sx={{ mt: 1 }}>{children}</Mui.Box> : null}
    </Mui.Box>
  </Mui.Stack>
);

// Label + helper used above a plain input inside a panel.
export const PanelFieldText = ({ title, helper }) => (
  <Mui.Box sx={{ minWidth: 0 }}>
    <Mui.Typography sx={fieldLabelSx}>
      {title}
    </Mui.Typography>
    {helper ? (
      <Mui.Typography sx={{ ...designType.helperText, mt: 0.2 }}>
        {helper}
      </Mui.Typography>
    ) : null}
  </Mui.Box>
);

// Bordered card with a tinted header strip. `accent` is { color, bg }, e.g.
// one of detailAccentPalette; teal when omitted.
export const PanelSectionCard = ({ icon, title, accent, action, children }) => (
  <Mui.Box
    component="section"
    sx={{
      border: "1px solid " + designColors.slate200,
      borderRadius: designRadii.card,
      overflow: "hidden",
      backgroundColor: designColors.white,
    }}
  >
    <Mui.Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        px: 2,
        py: 1.1,
        backgroundColor: accent?.bg || designColors.tealTint,
        borderBottom: "1px solid " + designColors.slate200,
      }}
    >
      <Mui.Box
        sx={{
          display: "flex",
          color: accent?.color || designColors.tealDark,
          "& svg": { fontSize: 20 },
        }}
      >
        {icon}
      </Mui.Box>
      <Mui.Typography
        component="h3"
        sx={{
          flex: 1,
          fontSize: 15,
          fontWeight: 600,
          color: designColors.slate900,
        }}
      >
        {title}
      </Mui.Typography>
      {action}
    </Mui.Stack>
    <Mui.Box sx={{ px: 2 }}>{children}</Mui.Box>
  </Mui.Box>
);

// One switch row inside a PanelSectionCard (or on its own). The ref goes to
// the switch, so react-hook-form's register() props can be spread on the row.
export const ToggleRow = React.forwardRef(
  ({ title, helper, isLast = true, ...switchProps }, ref) => (
  <Mui.Stack
    direction="row"
    spacing={2}
    alignItems="center"
    justifyContent="space-between"
    sx={{
      py: 1.5,
      borderBottom: isLast ? "none" : "1px solid " + designColors.slate100,
    }}
  >
    <PanelFieldText title={title} helper={helper} />
    <Mui.Switch
      ref={ref}
      inputProps={{ "aria-label": title }}
      sx={{ ...tealSwitchSx, flexShrink: 0 }}
      {...switchProps}
    />
  </Mui.Stack>
  ),
);
ToggleRow.displayName = "ToggleRow";
